/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * Calls of the event monitoring service: layouts and panels of the admin and the pages of events of every panel.
 * The new events arrive by the websocket topic. The pages of events are read without the overlay of the screen,
 * every panel shows its own loader while they arrive.
 */
import {createAsyncThunk} from "@reduxjs/toolkit";
import {apiInstance} from "../../Apis/Axios";
import {EVENT_MONITORING_SERVICE} from "../EndPoints";

const call = async (request) => {
    const result = await request
        .then(function (response) {
            return response;
        })
        .catch(function (error) {
            return error.response;
        });

    const {data, status} = result ?? {};

    return {data, status};
};

/**
 * Reads the layout loaded in the canvas, with its panels. The saved layouts are found by the simple search.
 */
export const GetEventMonitoringActiveLayout = createAsyncThunk(
    'eventMonitoringService/getEventMonitoringActiveLayout',
    async () => call(apiInstance.get(EVENT_MONITORING_SERVICE.getActiveLayout)),
);

/**
 * Creates a layout with the slots of its type and loads it in the canvas.
 */
export const CreateEventMonitoringLayout = createAsyncThunk(
    'eventMonitoringService/createEventMonitoringLayout',
    async (params) => call(apiInstance.post(EVENT_MONITORING_SERVICE.createLayout, params)),
);

/**
 * Renames a layout.
 */
export const UpdateEventMonitoringLayout = createAsyncThunk(
    'eventMonitoringService/updateEventMonitoringLayout',
    async (params) => call(apiInstance.put(EVENT_MONITORING_SERVICE.updateLayout, params)),
);

/**
 * Stores the layout loaded in the canvas and reads it with its panels.
 */
export const SetActiveEventMonitoringLayout = createAsyncThunk(
    'eventMonitoringService/setActiveEventMonitoringLayout',
    async (id) => call(apiInstance.put(`${EVENT_MONITORING_SERVICE.setActiveLayout}${id}`)),
);

/**
 * Deletes a layout with its panels.
 */
export const DeleteEventMonitoringLayout = createAsyncThunk(
    'eventMonitoringService/deleteEventMonitoringLayout',
    async (id) => call(apiInstance.delete(`${EVENT_MONITORING_SERVICE.deleteLayout}${id}`)),
);

/**
 * Stores that the admin saw the walkthrough, so it is not shown again. Whether it was seen is read with the
 * preferences of the user (GetUserPreferencesByUserId).
 */
export const SetEventMonitoringWalkthroughDone = createAsyncThunk(
    'eventMonitoringService/setEventMonitoringWalkthroughDone',
    async () => call(apiInstance.put(EVENT_MONITORING_SERVICE.setWalkthroughDone, null, { overlay: false })),
);

/**
 * Adds a panel at the end of a layout.
 */
export const CreateEventMonitoringPanel = createAsyncThunk(
    'eventMonitoringService/createEventMonitoringPanel',
    async (params) => call(apiInstance.post(EVENT_MONITORING_SERVICE.createPanel, params)),
);

/**
 * Changes the source of a panel.
 */
export const UpdateEventMonitoringPanel = createAsyncThunk(
    'eventMonitoringService/updateEventMonitoringPanel',
    async (params) => call(apiInstance.put(EVENT_MONITORING_SERVICE.updatePanel, params)),
);

/**
 * Stores the position and size of every panel of a layout.
 */
export const UpdateEventMonitoringPanelsGeometry = createAsyncThunk(
    'eventMonitoringService/updateEventMonitoringPanelsGeometry',
    async (params) => call(apiInstance.put(EVENT_MONITORING_SERVICE.updatePanelsGeometry, params)),
);

/**
 * Deletes a panel.
 */
export const DeleteEventMonitoringPanel = createAsyncThunk(
    'eventMonitoringService/deleteEventMonitoringPanel',
    async (id) => call(apiInstance.delete(`${EVENT_MONITORING_SERVICE.deletePanel}${id}`)),
);

/**
 * Reads a page of the newest events of the source of a panel, older than before.
 */
export const GetEventMonitoringPanelEvents = createAsyncThunk(
    'eventMonitoringService/getEventMonitoringPanelEvents',
    async ({ panelId, size, before, since }) => call(apiInstance.get(EVENT_MONITORING_SERVICE.getPanelEvents(panelId, size, before, since), { overlay: false })),
);

/**
 * Reads the areas of a zone panel with the counters of all their events. The zone overview counts the events
 * since the start of the day of the admin (since).
 */
export const GetEventMonitoringPanelAreas = createAsyncThunk(
    'eventMonitoringService/getEventMonitoringPanelAreas',
    async ({ panelId, since }) => call(apiInstance.get(EVENT_MONITORING_SERVICE.getPanelAreas(panelId, since), { overlay: false })),
);

/**
 * Reads a page of the newest events of an area of a zone panel, older than before.
 */
export const GetEventMonitoringPanelAreaEvents = createAsyncThunk(
    'eventMonitoringService/getEventMonitoringPanelAreaEvents',
    async ({ panelId, areaId, size, before, since }) => call(apiInstance.get(EVENT_MONITORING_SERVICE.getPanelAreaEvents(panelId, areaId, size, before, since), { overlay: false })),
);

/**
 * Reads a page of the newest reports of every user, started before the given date.
 */
export const GetEventMonitoringReports = createAsyncThunk(
    'eventMonitoringService/getEventMonitoringReports',
    async ({ size, before }) => call(apiInstance.get(EVENT_MONITORING_SERVICE.getReports(size, before), { overlay: false })),
);
